// src/components/ServiceNow/ServiceNowSection.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Divider,
  Typography,
} from "@mui/material";
import RitmOverview from "./RitmOverview";
import TaskDashboardWidgets from "./TaskDashboardWidgets";
import BulkActionsBar from "./BulkActionsBar";
import TaskCardList from "./TaskCardList";
import {
  getRitmWithTasks,
  getTaskStates,
  updateTask,
  bulkUpdateTasks,
  addCommentToTask,
  refreshRitmCache,
} from "services/userServices";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import { getValue } from "../../utils/serviceNowUtils";

const ServiceNowSection = ({ ritmNumber }) => {
  const [loading, setLoading] = useState(false);
  const [ritmData, setRitmData] = useState(null);
  const [taskStates, setTaskStates] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  // Fetch RITM data
  const fetchRitmData = async (showLoading = true) => {
    if (!ritmNumber || ritmNumber.trim() === "") {
      setRitmData(null);
      return;
    }

    try {
      if (showLoading) setLoading(true);
      setError(null);

      const data = await getRitmWithTasks(ritmNumber);

      // Check if response is an error
      if (data.response?.status === 404) {
        setError(`RITM ${ritmNumber} not found in ServiceNow`);
        setRitmData(null);
        showSnack(`RITM ${ritmNumber} not found in ServiceNow`, "error");
        return;
      }

      if (data.message || data.error) {
        setError(data.message || "Failed to fetch RITM data");
        setRitmData(null);
        showSnack("Error fetching ServiceNow data", "error");
        return;
      }

      setRitmData(data);
    } catch (err) {
      console.error("Error fetching RITM data:", err);
      setError("Failed to fetch RITM data");
      setRitmData(null);
      showSnack("Error fetching ServiceNow data", "error");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Fetch task states
  const fetchTaskStates = async () => {
    try {
      const states = await getTaskStates();
      if (states && !states.error) {
        setTaskStates(states);
      }
    } catch (err) {
      console.error("Error fetching task states:", err);
      // Don't show error to user, we can still function without state labels
    }
  };

  // Refresh RITM cache
  const handleRefresh = async () => {
    try {
      setLoading(true); // Show main loader
      await refreshRitmCache(ritmNumber);
      await fetchRitmData(false);
      showSnack("Data refreshed successfully", "success");
    } catch (err) {
      console.error("Error refreshing data:", err);
      showSnack("Error refreshing data", "error");
    } finally {
      setLoading(false); // Hide main loader
    }
  };

  // Handle task selection
  const handleTaskSelection = (taskSysId, isSelected) => {
    setSelectedTasks((prev) => {
      if (isSelected) {
        return [...prev, taskSysId];
      } else {
        return prev.filter((id) => id !== taskSysId);
      }
    });
  };

  // Handle select all - extract only sys_id strings
  const handleSelectAll = (isSelected) => {
    if (isSelected && ritmData?.Tasks) {
      // Extract just the sys_id string value, not the entire object
      const allTaskIds = ritmData.Tasks.map(
        (t) => getValue(t.sys_id) || t.sys_id,
      );
      setSelectedTasks(allTaskIds);
    } else {
      setSelectedTasks([]);
    }
  };

  // Handle toggle all expand/collapse
  const handleToggleAll = () => {
    setAllExpanded(!allExpanded);
  };

  // Handle task update with validation
  const handleTaskUpdate = async (taskSysId, updateData) => {
    try {
      setLoading(true);

      // Validate closing requirements if closing task
      if (isClosingTask(updateData.state)) {
        const validationError = await validateTaskClosing(
          taskSysId,
          updateData,
        );
        if (validationError) {
          showSnack(validationError, "error");
          return;
        }

        // Add default close notes if not provided
        addDefaultCloseNotes(updateData);
      }

      // Perform the update
      const result = await updateTask(taskSysId, updateData);

      if (result.error || result.message?.includes("error")) {
        showSnack(result.message || "Error updating task", "error");
        throw new Error(result.message || "Update failed");
      }

      showSnack("Task updated successfully", "success");
      await fetchRitmData(false);
    } catch (err) {
      console.error("Error updating task:", err);
      showSnack("Error updating task", "error");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Helper: Check if state is a closing state
  const isClosingTask = (state) => {
    return state === "3" || state === "4";
  };

  // Helper: Validate task closing requirements
  const validateTaskClosing = async (taskSysId, updateData) => {
    const task = ritmData?.Tasks?.find((t) => t.sys_id === taskSysId);

    if (!task) {
      return "Task not found";
    }

    // Check assignment requirement
    const assignmentError = validateTaskAssignment(task, updateData);
    if (assignmentError) {
      return assignmentError;
    }

    // Check notes requirement
    const notesError = validateTaskNotes(updateData);
    if (notesError) {
      return notesError;
    }

    return null; // No validation errors
  };

  // Helper: Validate task assignment
  const validateTaskAssignment = (task, updateData) => {
    const assignedToValue = getValue(task.assigned_to);

    if (!assignedToValue && !updateData.assigned_to) {
      return "Cannot close task: Task must be assigned before closing";
    }

    return null;
  };

  // Helper: Validate task notes
  const validateTaskNotes = (updateData) => {
    const hasNotes =
      updateData.work_notes || updateData.comments || updateData.close_notes;

    if (!hasNotes) {
      return "Cannot close task: Please add work notes or comments";
    }

    return null;
  };

  // Helper: Add default close notes if not provided
  const addDefaultCloseNotes = (updateData) => {
    if (!updateData.close_notes) {
      updateData.close_notes =
        updateData.state === "3"
          ? "Task completed successfully via ASTT"
          : "Task closed incomplete via ASTT";
    }
  };

  // Handle bulk update with validation
  const handleBulkUpdate = async (updateData) => {
    if (selectedTasks.length === 0) {
      showSnack("Please select tasks to update", "warning");
      return;
    }

    try {
      // Validate closing requirements for bulk operations
      if (updateData.state === "3" || updateData.state === "4") {
        // Check if any selected tasks are unassigned
        const unassignedTasks = ritmData?.Tasks?.filter(
          (t) => selectedTasks.includes(t.sys_id) && !getValue(t.assigned_to),
        );

        if (unassignedTasks && unassignedTasks.length > 0) {
          showSnack(
            `Cannot close tasks: ${unassignedTasks.length} selected task(s) are unassigned. Please assign them first.`,
            "error",
          );
          return;
        }

        // Ensure work notes or comments are provided
        if (!updateData.work_notes && !updateData.comments) {
          showSnack(
            "Cannot close tasks: Please add work notes or comments in the bulk update",
            "error",
          );
          return;
        }

        // Add close_notes if not provided
        if (!updateData.close_notes) {
          updateData.close_notes =
            updateData.state === "3"
              ? "Tasks completed successfully via ASTT bulk update"
              : "Tasks closed incomplete via ASTT bulk update";
        }
      }

      setLoading(true);
      const result = await bulkUpdateTasks({
        taskSysIds: selectedTasks,
        updateData: updateData,
      });

      if (result.error) {
        showSnack("Error updating tasks", "error");
        return;
      }

      showSnack(
        `${result.successfulUpdates} of ${result.totalRequested} tasks updated successfully`,
        "success",
      );
      setSelectedTasks([]); // Clear selection
      await fetchRitmData(false); // Refresh data
    } catch (err) {
      console.error("Error bulk updating tasks:", err);
      showSnack("Error updating tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle add comment
  const handleAddComment = async (taskSysId, comment, isWorkNote) => {
    try {
      setLoading(true); // Show main loader for every action

      const result = await addCommentToTask(taskSysId, {
        comment: comment,
        isWorkNote: isWorkNote,
      });

      // Proper response validation
      if (!result || result.error || result.response?.status >= 400) {
        const errorMsg =
          result.response?.data?.message ||
          result.message ||
          "Error adding comment";
        showSnack(errorMsg, "error");
        throw new Error(errorMsg);
      }

      showSnack("Comment added successfully", "success");
      await fetchRitmData(false); // Refresh data automatically
    } catch (err) {
      console.error("Error adding comment:", err);
      if (!err.message?.includes("Please enter")) {
        showSnack("Error adding comment", "error");
      }
      throw err;
    } finally {
      setLoading(false); // Hide main loader
    }
  };
  // Initial load
  useEffect(() => {
    let cancelled = false;

    const loadTaskStates = async () => {
      try {
        await fetchTaskStates();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch task states:", error);
        }
      }
    };

    loadTaskStates();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch RITM data when ritmNumber changes
  useEffect(() => {
    let cancelled = false;

    const loadRitmData = async () => {
      try {
        await fetchRitmData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch RITM data:", error);
        }
      }
    };

    loadRitmData();

    return () => {
      cancelled = true;
    };
  }, [ritmNumber]);

  // If no RITM number provided
  if (!ritmNumber || ritmNumber.trim() === "") {
    return (
      <Box sx={{ mt: 4, mb: 4 }}>
        <Divider sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#3299FF",
              fontWeight: 600,
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              letterSpacing: "0.10em",
            }}
          >
            ASKNOW TASK MANAGEMENT
          </Typography>
        </Divider>
        <Alert
          severity="info"
          sx={{
            mt: 2,
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          }}
        >
          Enter RITM number in the form above to view ServiceNow tasks
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading && !ritmData) {
    return (
      <Box sx={{ mt: 4, mb: 4 }}>
        <Divider sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#3299FF",
              fontWeight: 600,
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              letterSpacing: "0.10em",
            }}
          >
            SERVICENOW TASK MANAGEMENT
          </Typography>
        </Divider>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Error state (RITM not found)
  if (error && !ritmData) {
    return (
      <Box sx={{ mt: 4, mb: 4 }}>
        <Divider sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#3299FF",
              fontWeight: 600,
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              letterSpacing: "0.10em",
            }}
          >
            SERVICENOW TASK MANAGEMENT
          </Typography>
        </Divider>
        <Alert
          severity="error"
          sx={{
            mt: 2,
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Success state - display RITM and tasks
  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      {/* Section Divider */}
      <Divider sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: "#3299FF",
            fontWeight: 600,
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            letterSpacing: "0.10em",
          }}
        >
          SERVICENOW TASK MANAGEMENT
        </Typography>
      </Divider>

      {/* RITM Overview */}
      <RitmOverview
        ritm={ritmData?.Ritm}
        statistics={ritmData?.Statistics}
        cachedAt={ritmData?.CachedAt}
        onRefresh={handleRefresh}
        loading={loading}
        taskStates={taskStates}
      />

      {/* Dashboard Widgets */}
      <TaskDashboardWidgets statistics={ritmData?.Statistics} />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        totalTasks={ritmData?.Tasks?.length || 0}
        selectedTasks={selectedTasks}
        onSelectAll={handleSelectAll}
        onBulkUpdate={handleBulkUpdate}
        onRefresh={handleRefresh}
        onToggleAll={handleToggleAll}
        taskStates={taskStates}
        loading={loading}
        allExpanded={allExpanded}
      />

      {/* Task Cards */}
      <TaskCardList
        tasks={ritmData?.Tasks || []}
        selectedTasks={selectedTasks}
        onTaskSelection={handleTaskSelection}
        onTaskUpdate={handleTaskUpdate}
        onAddComment={handleAddComment}
        taskStates={taskStates}
        allExpanded={allExpanded}
      />
    </Box>
  );
};

export default ServiceNowSection;
